import {
	Box,
	Button,
	CardActions,
	CardContent,
	Chip,
	Divider,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppCounterAdd from '@/common/components/form-inputs/AppGetNextNumber';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	CONSENT,
	LANGUAGE_ZH_LIST,
	SEX_LIST,
	ZH_SEX_LIST,
	ZH_YESNO,
} from '@/common/constants/common';
import {
	useGetAllEnumListQuery,
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
	useGetStaffAllListQuery,
} from '@/api/common';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { eightDigitPhone } from '@/utils/validationHelper';
import dayjs from 'dayjs';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { Language } from '@/api/enum/common.enum';
import { AreaDTO } from '@/api/DTO/common.interface';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import {
	useChangeFamilyNextFileNoMutation,
	useCheckClosedFamilyMemberDuplicationMutation,
	useCheckFamilyMemberDuplicationMutation,
	useCheckFamilyMemberMemberDuplicationMutation,
	useCreateFamilyMutation,
	useGetFamilyGroupListMutation,
	useGetFamilyNextFileNoQuery,
	useGetFamilyToCreateQuery,
} from '@/api/family-management/familyApi';
import {
	ClosedFamilyMemberRef,
	FamilyMemberDuplicationCheckDTO,
	FamilyRequest,
} from '@/api/DTO/family/family.interface';
import SelectMemberDialog from './SelectMemberDialog';
import FamilyMemberDuplicateDialog from './FamilyMemberDuplicateDialog';
import FamilyClosedDuplicateDialog, {
	ClosedFamilyMemberChoice,
} from './FamilyClosedDuplicateDialog';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { FamilyMemberMemberDuplicationCheckDTO } from '@/api/DTO/member-management/memberDuplication';
import LinkCandidateDialog, {
	LinkCandidateChoice,
} from '@/common/components/identity-link/LinkCandidateDialog';
import LinkConfirmDialog from '@/common/components/identity-link/LinkConfirmDialog';
import { useMemberLinkColumns } from './useMemberLinkColumns';
import { useLoginAccountRegisterPrompt } from '@/common/components/program-app-login/useLoginAccountRegisterPrompt';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageOnDate,
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';

const ADULT_AGE = 18;

interface Props {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	/** CHA-004: closed family member to link on creation (picked or from the check). */
	closedFamilyMember: ClosedFamilyMemberRef | null;
	setClosedFamilyMember: (value: ClosedFamilyMemberRef | null) => void;
}

export default function FamilyInfoForm({
	type,
	setType,
	closedFamilyMember,
	setClosedFamilyMember,
}: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [createFamily] = useCreateFamilyMutation();
	const promptLoginAccount = useLoginAccountRegisterPrompt();
	const [getFamilyGroupList, { isLoading: isPirGroupLoading }] =
		useGetFamilyGroupListMutation();
	const { familyUserId } = useParams();
	const [yearList, setYearList] = useState<any>([]);

	const [getAreaSubItemList] = useGetAreaSubItemListMutation();
	const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
	const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
	const [estateCourtVillageList, setEstateCourtVillageList] = useState<
		AreaDTO[]
	>([]);
	const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);
	const [changeFamilyNextFileNo] = useChangeFamilyNextFileNoMutation();

	const userInfo = useSelector(selectMyInfo);

	// CHA-003: admitted family members of this centre that may be the same person.
	const [checkFamilyMemberDuplication] =
		useCheckFamilyMemberDuplicationMutation();
	const [familyDuplicates, setFamilyDuplicates] = useState<
		FamilyMemberDuplicationCheckDTO[]
	>([]);
	const familyDuplicateResolver = useRef<
		((confirmed: boolean) => void) | undefined
	>(undefined);

	/** Resolves `true` when there is no hit or staff confirm "not the same person". */
	const confirmNoFamilyDuplicate = async (data: FamilyRequest) => {
		const response = await checkFamilyMemberDuplication({
			nameZh: data.nameZh,
			nameEn: data.nameEn,
			phone: data.phone,
			hkid: data.hkid,
		});
		if (!('data' in response) || !response.data?.success) return false;
		const hits = response.data.data ?? [];
		if (hits.length === 0) return true;
		return new Promise<boolean>((resolve) => {
			familyDuplicateResolver.current = resolve;
			setFamilyDuplicates(hits);
		});
	};

	const handleFamilyDuplicateClose = (confirmed: boolean) => {
		setFamilyDuplicates([]);
		familyDuplicateResolver.current?.(confirmed);
		familyDuplicateResolver.current = undefined;
	};

	// CHA-004 §三: closed family members of this centre that may be the same person.
	const [checkClosedFamilyMemberDuplication] =
		useCheckClosedFamilyMemberDuplicationMutation();
	const [closedDuplicates, setClosedDuplicates] = useState<
		FamilyMemberDuplicationCheckDTO[]
	>([]);
	const closedDuplicateResolver = useRef<
		((choice: ClosedFamilyMemberChoice) => void) | undefined
	>(undefined);

	const chooseClosedFamilyMember = async (
		data: FamilyRequest
	): Promise<ClosedFamilyMemberChoice> => {
		const response = await checkClosedFamilyMemberDuplication({
			nameZh: data.nameZh,
			nameEn: data.nameEn,
			phone: data.phone,
			hkid: data.hkid,
		});
		if (!('data' in response) || !response.data?.success)
			return { action: 'cancel' };
		const hits = response.data.data ?? [];
		if (hits.length === 0) return { action: 'skip' };
		return new Promise<ClosedFamilyMemberChoice>((resolve) => {
			closedDuplicateResolver.current = resolve;
			setClosedDuplicates(hits);
		});
	};

	const handleClosedDuplicateClose = (choice: ClosedFamilyMemberChoice) => {
		setClosedDuplicates([]);
		closedDuplicateResolver.current?.(choice);
		closedDuplicateResolver.current = undefined;
	};

	// CHA-005 §四: admitted members that may be the same person as the applicant.
	const [checkFamilyMemberMemberDuplication] =
		useCheckFamilyMemberMemberDuplicationMutation();
	const memberLinkColumns = useMemberLinkColumns();
	const [memberCandidates, setMemberCandidates] = useState<
		FamilyMemberMemberDuplicationCheckDTO[]
	>([]);
	const memberCandidateResolver = useRef<
		| ((
				choice: LinkCandidateChoice<FamilyMemberMemberDuplicationCheckDTO>
		  ) => void)
		| undefined
	>(undefined);

	const chooseMemberCandidate = (
		rows: FamilyMemberMemberDuplicationCheckDTO[]
	) =>
		new Promise<LinkCandidateChoice<FamilyMemberMemberDuplicationCheckDTO>>(
			(resolve) => {
				memberCandidateResolver.current = resolve;
				setMemberCandidates(rows);
			}
		);

	const handleMemberCandidateClose = (
		choice: LinkCandidateChoice<FamilyMemberMemberDuplicationCheckDTO>
	) => {
		setMemberCandidates([]);
		memberCandidateResolver.current?.(choice);
		memberCandidateResolver.current = undefined;
	};

	// CHA-005 §五: the member record to link, shown with the fields it will get.
	const [linkTarget, setLinkTarget] = useState<{
		id: string;
		fileNo?: string;
		inGroup?: boolean;
	} | null>(null);
	const linkConfirmResolver = useRef<
		((confirmed: boolean) => void) | undefined
	>(undefined);

	const confirmLink = (target: {
		id: string;
		fileNo?: string;
		inGroup?: boolean;
	}) =>
		new Promise<boolean>((resolve) => {
			linkConfirmResolver.current = resolve;
			setLinkTarget(target);
		});

	const handleLinkConfirmClose = (confirmed: boolean) => {
		setLinkTarget(null);
		linkConfirmResolver.current?.(confirmed);
		linkConfirmResolver.current = undefined;
	};

	const isInFamilyGroup = async (userId: string) => {
		const response = await getFamilyGroupList(userId);
		return 'data' in response && (response.data?.data || []).length > 0;
	};

	/**
	 * Member `userId` to link (`null` = create without linking) and whether that
	 * member is already in a family group; `undefined` = cancelled.
	 */
	const chooseLinkedMember = async (
		data: FamilyRequest
	): Promise<{ userId: string | null; inGroup: boolean } | undefined> => {
		// Created from a member record (§2.2): always linked to that member.
		if (familyUserId)
			return (await confirmLink({ id: familyUserId }))
				? { userId: familyUserId, inGroup: applicantInGroup }
				: undefined;
		const response = await checkFamilyMemberMemberDuplication({
			nameZh: data.nameZh,
			nameEn: data.nameEn,
			phone: data.phone,
			hkid: data.hkid,
		});
		if (!('data' in response) || !response.data?.success) return undefined;
		const hits = response.data.data ?? [];
		if (hits.length === 0) return { userId: null, inGroup: false };
		// "Back" on the confirmation returns to the candidate list.
		for (;;) {
			const choice = await chooseMemberCandidate(hits);
			if (choice.action === 'cancel') return undefined;
			if (choice.action === 'skip') return { userId: null, inGroup: false };
			// CHA-006 §七: a member already in a family group keeps that group, so
			// the new family member does not join the PIR's group.
			const inGroup = await isInFamilyGroup(choice.record.id);
			if (
				await confirmLink({
					id: choice.record.id,
					fileNo: choice.record.fileNo,
					inGroup,
				})
			)
				return { userId: choice.record.id, inGroup };
		}
	};

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
	});

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);
	const { data: details }: any = useGetFamilyToCreateQuery(familyUserId || '', {
		refetchOnMountOrArgChange: true,
		skip: !familyUserId,
	});

	// CHA-006 §七: an applicant already in a family group keeps that group; the
	// new family member does not join the PIR's group.
	const [applicantInGroup, setApplicantInGroup] = useState(false);
	useEffect(() => {
		if (!familyUserId) return;
		isInFamilyGroup(familyUserId).then(setApplicantInGroup);
	}, [familyUserId]);

	const { data: areaItemList } = useGetAreaItemListQuery(
		{ type: 0 },
		{ refetchOnMountOrArgChange: true }
	);

	const { data: nextFileNo } = useGetFamilyNextFileNoQuery(
		{ fiscalYear: '' },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: workDicList } = useGetDicListQuery(
		'family_member_work_status',
		{ refetchOnMountOrArgChange: true }
	);

	const { data: workStatusDicList } = useGetDicListQuery('work_status', {
		refetchOnMountOrArgChange: true,
	});

	const { data: educationDicList } = useGetDicListQuery('education', {
		refetchOnMountOrArgChange: true,
	});

	/**
	 * CHA-010 §三: under 18 on the applicant signature date (before it is entered,
	 * the guardian signature date or today). An unknown age counts as adult.
	 */
	const isMinorAtSigning = (values: any) => {
		const age = ageOnDate(
			values,
			values?.applicationSignatureDate ||
				values?.guardianSignatureDate ||
				undefined
		);
		return age !== null && age < ADULT_AGE;
	};

	const defaultValues: DefaultValues<any> = {
		addressLanguage: Language.chinese,
		twcMember: null,
		fwcMember: null,
		fssMember: null,
		familyNetworkMember: null,
		acceptPhoneCalls: null,
		acceptMails: null,
		acceptEmails: null,
		acceptTwcCommunications: null,
		acceptFwcCommunications: null,
		acceptFssCommunications: null,
		acceptFamilyNetworkCommunications: null,
		pirType: null,
		sex: null,
		acceptApplication: null,
		consentFamilyNetwork: null,
		consentTWC: null,
		consentPersonalDataUsage: null,
		...toAgeFormValues(),
	};

	const formSchema = Yup.object().shape({
		fileNoC: Yup.string().required(t('errorMessage.pleaseEnter')),
		twcMember: Yup.string().required(t('errorMessage.pleaseSelect')),
		fwcMember: Yup.string().required(t('errorMessage.pleaseSelect')),
		fssMember: Yup.string().required(t('errorMessage.pleaseSelect')),
		familyNetworkMember: Yup.string().required(t('errorMessage.pleaseSelect')),
		acceptPhoneCalls: Yup.string().required(t('errorMessage.pleaseSelect')),
		acceptMails: Yup.string().required(t('errorMessage.pleaseSelect')),
		acceptEmails: Yup.string().required(t('errorMessage.pleaseSelect')),
		acceptTwcCommunications: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		acceptFwcCommunications: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		acceptFssCommunications: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		acceptFamilyNetworkCommunications: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		pirType: Yup.string().required(t('errorMessage.pleaseSelect')),
		nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
		nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
		sex: Yup.string().required(t('errorMessage.pleaseSelect')),
		hkid: Yup.string().required(t('errorMessage.pleaseEnter')),
		...ageSchemaFields(t, {
			getOriginalEstimatedAge: () => details?.data?.estimatedAge,
		}),
		familyMemberWorkStatus: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		phone: Yup.string()
			.required(t('errorMessage.pleaseEnter'))
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		homeTel: Yup.string()
			.nullable()
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
		consentPersonalDataUsage: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		consentTWC: Yup.string().required(t('errorMessage.pleaseSelect')),
		consentFamilyNetwork: Yup.string().required(t('errorMessage.pleaseSelect')),
		// CHA-010: an adult signs the form, a minor's parent / guardian does.
		applicationSignatureDate: Yup.string()
			.nullable()
			.test({
				name: 'requiredForAdult',
				message: t('errorMessage.pleaseSelect'),
				test: (value, ctx) => !!value || isMinorAtSigning(ctx.parent),
			})
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		guardianSignatureDate: Yup.string()
			.nullable()
			.test({
				name: 'requiredForMinor',
				message: t('errorMessage.pleaseSelect'),
				test: (value, ctx) => !!value || !isMinorAtSigning(ctx.parent),
			})
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		guardianName: Yup.string().nullable(),
		handledBy: Yup.string().required(t('errorMessage.pleaseSelect')),
		handlingSignatureDate: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		acceptApplication: Yup.string().required(t('errorMessage.pleaseSelect')),
		nonAcceptanceReason: Yup.string().when('acceptApplication', {
			is: (acceptApplication: any) =>
				acceptApplication === false || acceptApplication === 'false',
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		workerSignatureDate: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		pirNameZh: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		pirSex: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		pirDateOfBirth: Yup.string()
			.when('pirType', {
				is: (pirType: number) => pirType == 1,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			})
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		pirUserId: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 0,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		relationshipToPir: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 0 || pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		// Only used to link the PIR in the family group (CHA-006 §七).
		relationshipToApplicant: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 0 && !applicantInGroup,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		liveWithPir: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 0 || pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		// Cleared unless the new family member joins the PIR's family group.
		familyMemberApplicationRelationshipList: Yup.array().of(
			Yup.object().shape({
				relationshipToMember: Yup.string().required(
					t('errorMessage.pleaseSelect')
				),
				relationshipToApplicant: Yup.string().required(
					t('errorMessage.pleaseSelect')
				),
			})
		),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const {
		fields: familyGroupFields,
		append: familyGroupAppend,
		remove: familyGroupRemove,
		update: familyGroupUpdate,
	}: any = useFieldArray({
		control: methods.control,
		name: 'familyMemberApplicationRelationshipList',
	});

	useEffect(() => {
		if (details) {
			methods.reset({ ...details.data, ...toAgeFormValues(details.data) });

			if (details.data.region) initAddress(details.data.region, 'region');
			if (details.data.district) initAddress(details.data.district, 'district');
			if (details.data.subdistrict)
				initAddress(details.data.subdistrict, 'subdistrict');
			if (details.data.estateCourtVillage)
				initAddress(details.data.estateCourtVillage, 'estateCourtVillage');
		}
	}, [details]);

	useEffect(() => {
		if (nextFileNo) {
			const parts = nextFileNo.data.split('/');
			methods.setValue('fileNoA', `${parts[0]}/${parts[1]}/${parts[2]}`);
			methods.setValue('fileNoB', parts[3]);
			methods.setValue('fileNoC', parts[4]);
			const years = parts[3].split('-');
			setYearList([
				{
					id: `${years[0] - 1}-${years[0]}`,
					name: `${years[0] - 1}-${years[0]}`,
				},
				{ id: parts[3], name: parts[3] },
				{
					id: `${years[1]}-${Number(years[1]) + 1}`,
					name: `${years[1]}-${Number(years[1]) + 1}`,
				},
			]);
		}
	}, [nextFileNo, details]);

	const watchAddressLanguage = methods.watch('addressLanguage');
	const watchPirType = methods.watch('pirType');
	const watchPirUserId = methods.watch('pirUserId');
	const isMemberPir = watchPirType == '0';
	const [pirMember, setPirMember] = useState<{
		id: string;
		fileNo?: string;
		familyMemberFileNo?: string;
		nameEn?: string;
		nameZh?: string;
	} | null>(null);
	const watchRelationshipToPir = methods.watch('relationshipToPir');
	const watchAcceptApplication = methods.watch('acceptApplication');
	const watchAgeFields = methods.watch([
		'ageSource',
		'dateOfBirth',
		'estimatedAge',
		'applicationSignatureDate',
		'guardianSignatureDate',
	]);
	const isMinor = isMinorAtSigning({
		ageSource: watchAgeFields[0],
		dateOfBirth: watchAgeFields[1],
		estimatedAge: watchAgeFields[2],
		applicationSignatureDate: watchAgeFields[3],
		guardianSignatureDate: watchAgeFields[4],
	});

	// Which signature date is required follows the age, so recheck both.
	useEffect(() => {
		if (methods.formState.isSubmitted)
			methods.trigger(['applicationSignatureDate', 'guardianSignatureDate']);
	}, [isMinor]);

	useEffect(() => {
		const subscription = methods.watch((value, field) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<FamilyRequest> = async (data) => {
		// CHA-004 §八: in-progress family members → closed family members → members.
		if (!(await confirmNoFamilyDuplicate(data))) return;
		let closedFamilyMemberId = closedFamilyMember?.id;
		if (!closedFamilyMemberId) {
			const choice = await chooseClosedFamilyMember(data);
			if (choice.action === 'cancel') return;
			if (choice.action === 'link') {
				closedFamilyMemberId = choice.closedFamilyMember.id;
				// Shown on the banner, so a retry keeps the link without asking again.
				setClosedFamilyMember(choice.closedFamilyMember);
			}
		}
		const linked = await chooseLinkedMember(data);
		if (linked === undefined) return;
		const parts = nextFileNo?.data.split('/');
		const obj: FamilyRequest = {
			...data,
			...toAgePayload(data),
			familyMemberFileNo: `${parts[0]}/${parts[1]}/${parts[2]}/${data.fileNoB}/${data.fileNoC}`,
			pirUserId: data.pirType == 0 ? data.pirUserId : '',
			familyMemberApplicationRelationshipList:
				linked.inGroup || data.pirType != 0
					? []
					: data.familyMemberApplicationRelationshipList,
			...(closedFamilyMemberId ? { closedFamilyMemberId } : {}),
			// CHA-005 §五: links to this member; the form values win on the synced fields.
			userId: linked.userId || undefined,
		};
		const response = await createFamily(obj);
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createFamilySuccess')
					: t('snackbar.updateUserSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/family-management/family-management-list');
				promptLoginAccount(response.data?.data?.id || '', 'family-member');
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) {
			// navigate(`/user-management/user-management-list/detail/${userId}`);
		} else {
			navigate('/family-management/family-management-list');
		}
	};

	const initAddress = async (pid: string, type: string) => {
		if (!pid) return;
		const response = await getAreaSubItemList(pid || '');
		if ('data' in response) {
			if (type == 'region') setDistrictList(response?.data?.data || []);
			if (type == 'district') setSubDistrictList(response?.data?.data || []);
			if (type == 'subdistrict')
				setEstateCourtVillageList(response?.data?.data || []);
			if (type == 'estateCourtVillage')
				setBuildingBlockList(response?.data?.data || []);
		}
	};

	// Resolve an area item's label in the currently selected address language.
	const resolveAreaLabel = (list: any, id: any) =>
		(list || []).find((i: any) => i.id == id)?.[
			watchAddressLanguage == Language.chinese ? 'nameZh' : 'nameEn'
		];

	const handleChangeRegion = async (e: any, type: string) => {
		if (!e) return;
		const response = await getAreaSubItemList(e || '');

		if ('data' in response) {
			if (type == 'region') {
				methods.setValue('district', '');
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setDistrictList(response?.data?.data || []);
			}
			if (type == 'district') {
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setSubDistrictList(response?.data?.data || []);
			}
			if (type == 'subdistrict') {
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setEstateCourtVillageList(response?.data?.data || []);
			}
			if (type == 'estateCourtVillage') {
				methods.setValue('buildingBlock', '');
				setBuildingBlockList(response?.data?.data || []);
			}
		}
	};

	// CHA-006 §7.2: backend snapshots these from pirUserId on submit; prefilled for display only.
	const getSelectMemberRow = async (row: any) => {
		setPirMember(row);
		methods.setValue('pirUserId', row.id, { shouldValidate: true });
		methods.setValue('pirNameZh', row.nameZh || '');
		methods.setValue('pirSex', row.sex ?? '');
		methods.setValue(
			'pirDateOfBirth',
			row.dateOfBirth ? dayjs(row.dateOfBirth).format(API_DATE_FORMAT) : ''
		);
		methods.setValue('pirUnit', row.branchNameZh || row.branchDisplayName || '');
		methods.setValue('pirFileNo', row.fileNo || '');
		methods.setValue('familyMemberApplicationRelationshipList', []);
		if (applicantInGroup) return;

		const response = await getFamilyGroupList(row.id);
		methods.setValue(
			'familyMemberApplicationRelationshipList',
			response?.data?.data || []
		);
	};

	const relationshipOptions = (relationshipDicList?.data || []).map(
		(item: any) => ({
			...item,
			name: item.labelZh,
		})
	);

	const columns2: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('family.familyGroupMember'),
			flex: 20,
			renderCell: (params) => {
				const { row } = params;
				const fileNos = [
					row.targetMemberFileNo && {
						fileNo: row.targetMemberFileNo,
						href: row.targetMemberIdDesc
							? `/member-management/member-list/detail/${row.targetMemberIdDesc}`
							: undefined,
					},
					row.targetFamilyMemberFileNo && {
						fileNo: row.targetFamilyMemberFileNo,
						href: row.targetFamilyMemberIdDesc
							? `/family-management/family-management-list/detail/${row.targetFamilyMemberIdDesc}`
							: undefined,
					},
				].filter(Boolean) as { fileNo: string; href?: string }[];
				// Same layout as the Manage family member pair dialog.
				return (
					<Box>
						<Typography variant='body2'>
							{row.targetMemberNameEn || '-'}
							{row.isPir && (
								<Chip
									label={t('family.pirChip')}
									color='chipPrimary'
									size='small'
									sx={{ marginLeft: '8px' }}
								/>
							)}
						</Typography>
						<Typography variant='body2'>{row.targetMemberNameZh}</Typography>
						{fileNos.length > 0 && (
							<Typography variant='caption' color='common.neutral40'>
								{fileNos.map((item, i) => (
									<span key={item.fileNo}>
										{i > 0 && ' / '}
										{item.href ? (
											<a href={item.href} target='_blank' rel='noopener noreferrer'>
												{item.fileNo}
											</a>
										) : (
											item.fileNo
										)}
									</span>
								))}
							</Typography>
						)}
					</Box>
				);
			},
		},
		{
			field: 'relationshipToMember',
			headerName: t('family.applicantToMember'),
			flex: 15,
			renderCell: (params: any) =>
				// Same as 申請者為精神復元人士的 above, which the staff fills in once.
				params.row.isPir ? (
					<Typography variant='body2' color='common.neutral40'>
						{relationshipOptions.find(
							(item: any) => item.id == watchRelationshipToPir
						)?.name || t('family.pirRelationshipFromAbove')}
					</Typography>
				) : (
					<AppDropDownMenu
						control={methods.control}
						name={`familyMemberApplicationRelationshipList[${params.row.index}].relationshipToMember`}
						label={t('family.relationship') + '*'}
						options={relationshipOptions}
					/>
				),
		},
		{
			field: 'relationshipToApplicant',
			headerName: t('family.memberToApplicant'),
			flex: 15,
			renderCell: (params: any) => (
				<AppDropDownMenu
					control={methods.control}
					name={
						params.row.isPir
							? 'relationshipToApplicant'
							: `familyMemberApplicationRelationshipList[${params.row.index}].relationshipToApplicant`
					}
					label={t('family.relationship') + '*'}
					options={relationshipOptions}
				/>
			),
		},
	];

	// The form only stores coded values (region, education ...). The link
	// confirmation shows the *Desc labels, so resolve the current form codes to
	// their labels here, where all the lookup lists live.
	const getDetailsWithDesc = () => {
		const v = methods.getValues();
		return {
			...v,
			regionDesc: resolveAreaLabel(areaItemList?.data, v.region),
			districtDesc: resolveAreaLabel(districtList, v.district),
			subdistrictDesc: resolveAreaLabel(subDistrictList, v.subdistrict),
			estateCourtVillageDesc: resolveAreaLabel(
				estateCourtVillageList,
				v.estateCourtVillage
			),
			buildingBlockDesc: resolveAreaLabel(buildingBlockList, v.buildingBlock),
			educationDesc: (educationDicList?.data || []).find(
				(i: any) => i.id == v.education
			)?.labelEn,
		};
	};

	return (
		<>
			<FamilyMemberDuplicateDialog
				open={familyDuplicates.length > 0}
				rows={familyDuplicates}
				onClose={handleFamilyDuplicateClose}
			/>
			<FamilyClosedDuplicateDialog
				open={closedDuplicates.length > 0}
				rows={closedDuplicates}
				onClose={handleClosedDuplicateClose}
			/>
			<LinkCandidateDialog
				open={memberCandidates.length > 0}
				title={t('identityLink.memberCandidateTitle')}
				text={t('identityLink.memberCandidateText')}
				rows={memberCandidates}
				columns={memberLinkColumns}
				canLink={(row) => !row.linkedFamilyMemberId}
				cannotLinkTips={t('identityLink.alreadyLinkedTips')}
				onClose={handleMemberCandidateClose}
			/>
			<LinkConfirmDialog
				open={!!linkTarget}
				direction='toMember'
				targetId={linkTarget?.id}
				targetFileNo={linkTarget?.fileNo}
				source={linkTarget ? getDetailsWithDesc() : {}}
				notice={
					// The form already shows this hint when created from a member record.
					!familyUserId && linkTarget?.inGroup && watchPirType == 0
						? t('family.linkedMemberInGroupPirHint')
						: undefined
				}
				onClose={handleLinkConfirmClose}
			/>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.serviceDetails')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							{' '}
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.memberNo') + '*'}
								</Typography>
							</Grid>
							<Grid size={1.8}>
								<AppTextField
									control={methods.control}
									name='fileNoA'
									label={''}
									disabled
								/>
							</Grid>
							<span style={{ verticalAlign: 'middle' }}>/</span>
							<Grid size={1.8}>
								<AppDropDownMenu
									control={methods.control}
									name='fileNoB'
									label={''}
									options={yearList}
									handleChange={async (e: any) => {
										if (e) {
											const response = await changeFamilyNextFileNo({
												fiscalYear: e,
											});
											if ('data' in response) {
												const parts = response.data?.data.split('/');
												methods.setValue(
													'fileNoA',
													`${parts[0]}/${parts[1]}/${parts[2]}`
												);
												methods.setValue('fileNoB', parts[3]);
												methods.setValue('fileNoC', parts[4]);
												const years = parts[3].split('-');
												setYearList([
													{
														id: `${years[0] - 1}-${years[0]}`,
														name: `${years[0] - 1}-${years[0]}`,
													},
													{ id: parts[3], name: parts[3] },
													{
														id: `${years[1]}-${Number(years[1]) + 1}`,
														name: `${years[1]}-${Number(years[1]) + 1}`,
													},
												]);
											}
										}
									}}
								/>
							</Grid>
							/
							<Grid size={1.8}>
								<AppCounterAdd
									control={methods.control}
									name='fileNoC'
									// Family file numbers have an extra leading segment (fileNoA
									// spans parts[0..2]), so the counter sits at parts[4]. Drop the
									// first segment so AppCounterAdd's fixed parts[3] lookup lands on
									// the counter instead of the fiscal year.
									nextFileNo={nextFileNo?.data?.split('/').slice(1).join('/')}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('family.tobeMember')}
								</Typography>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.twc') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`twcMember`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.fwc') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`fwcMember`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.familySupportServices') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`fssMember`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.familyAlliance') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`familyNetworkMember`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body1'>{t('family.accept')}</Typography>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.phone') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptPhoneCalls`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.mail') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptMails`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.email') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptEmails`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.twc') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptTwcCommunications`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.fwc') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptFwcCommunications`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.familySupportServices') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptFssCommunications`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.familyAlliance') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptFamilyNetworkCommunications`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.rehabiliteeInfo')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.rehabiliteeMember') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`pirType`}
									row
									options={(allEnumList?.data.PIRTypeEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.descZh,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('pirNameZh', '');
										methods.setValue('pirSex', '');
										methods.setValue('pirDateOfBirth', '');
										methods.setValue('pirUnit', '');
										methods.setValue('pirFileNo', '');
										methods.setValue('relationshipToPir', '');
										methods.setValue('liveWithPir', '');
										methods.setValue('relationshipToApplicant', '');
										methods.setValue('pirUserId', '');
										methods.setValue('familyMemberApplicationRelationshipList', []);
										setPirMember(null);
										// Errors only show once the staff submits.
										methods.clearErrors([
											'pirNameZh',
											'pirSex',
											'pirDateOfBirth',
											'relationshipToPir',
											'relationshipToApplicant',
											'liveWithPir',
											'pirUserId',
										]);
									}}
								/>
							</Grid>
							{isMemberPir && (
								<Grid size={12}>
									<SelectMemberDialog
										getSelectMemberRow={getSelectMemberRow}
										pirApplicantId={familyUserId}
										selectedMember={pirMember}
									/>
									{methods.formState.errors.pirUserId && (
										<Typography
											variant='caption'
											color='error'
											component='p'
											marginTop='4px'
										>
											{methods.formState.errors.pirUserId.message as string}
										</Typography>
									)}
								</Grid>
							)}
							{(watchPirType == '0' || watchPirType == 1) && (
								<>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='pirNameZh'
											label={t('family.pirNameZh') + '*'}
											disabled={isMemberPir}
										/>
									</Grid>
									<Grid size={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('preAdmission.zh_sex') + '*'}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name='pirSex'
											row
											options={ZH_SEX_LIST}
											disabled={isMemberPir}
										/>
									</Grid>
									<Grid size={6}>
										<AppDatePicker
											maximumDate={dayjs()
												.startOf('day')
												.format(API_DATE_FORMAT)}
											control={methods.control}
											name='pirDateOfBirth'
											label={t('preAdmission.birthday') + '*'}
											disabled={isMemberPir}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='pirUnit'
											label={t('family.pirUnit')}
											disabled={isMemberPir}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='pirFileNo'
											label={t('family.pirFileNo')}
											disabled={isMemberPir}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											control={methods.control}
											name='relationshipToPir'
											label={t('family.relationshipToPir') + '*'}
											options={relationshipOptions}
										/>
									</Grid>
									<Grid size={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('family.liveWithPir') + '*'}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name='liveWithPir'
											row
											options={ZH_YESNO}
										/>
									</Grid>
								</>
							)}

							{/* CHA-006 §七: the family group the new family member joins, apart from the form itself. */}
							{isMemberPir &&
								(applicantInGroup || !!watchPirUserId) &&
								!isPirGroupLoading && (
									<Grid size={12}>
										{/* Outlined: an extension of the form, not part of the submitted form itself. */}
										<Box
											sx={{
												border: '1px solid',
												borderColor: 'common.neutral90',
												borderRadius: '16px',
												bgcolor: 'background.default',
												padding: 2,
											}}
										>
											<Typography variant='body1'>
												{t('family.joinFamilyGroup')}
											</Typography>
											<Typography variant='body2' color='common.neutral40'>
												{applicantInGroup
													? t('family.applicantInGroupPirHint')
													: familyGroupFields.length > 0
														? t('family.joinPirFamilyGroupText')
														: t('family.pirNoFamilyGroupText')}
											</Typography>
											{!applicantInGroup && pirMember && (
												<Box
													marginTop={2}
													bgcolor='common.white'
													borderRadius='8px'
													overflow='hidden'
												>
													<LocalDataGrid
														rows={[
															{
																id: `pir-${pirMember.id}`,
																isPir: true,
																targetMemberIdDesc: pirMember.id,
																targetMemberNameEn: pirMember.nameEn,
																targetMemberNameZh: pirMember.nameZh,
																targetMemberFileNo: pirMember.fileNo,
																targetFamilyMemberFileNo: pirMember.familyMemberFileNo,
															},
															...familyGroupFields.map((item: any, index: any) => ({
																...item,
																index,
															})),
														]}
														columns={columns2}
														getRowId={(row) => row.id}
														pagination={false}
													/>
												</Box>
											)}
										</Box>
									</Grid>
								)}
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.applicantInfo')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.zh_name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.en_name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.zh_sex') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={ZH_SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkid'
									label={t('preAdmission.idNumber') + '*'}
								/>
							</Grid>
							<AppAgeInput control={methods.control} lang='zh' />
							<Grid size={6}>
								<AppDropDownMenu
									control={methods.control}
									name='familyMemberWorkStatus'
									label={t('family.getJob') + '*'}
									options={(workDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='familyMemberOccupation'
									label={t('preAdmission.occupation')}
									options={(workStatusDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='education'
									label={t('preAdmission.zh_education')}
									options={(educationDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='phone'
									label={t('preAdmission.phone') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='homeTel'
									label={t('preAdmission.homePhone')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='email'
									label={t('preAdmission.zh_email')}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.zh_addressLanguage') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='addressLanguage'
									row
									options={LANGUAGE_ZH_LIST}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.territory')}
											options={(areaItemList?.data || []).map(
												(item: { nameEn: any }) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.area')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.village')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}
							{watchAddressLanguage == Language.chinese && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.territory')}
											options={(areaItemList?.data || []).map(
												(item: { nameZh: any }) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.area')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.village')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreet'
									label={t('preAdmission.street')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreetNo'
									label={t('preAdmission.streetNumber')}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameEn,
											};
										})}
									/>
								</Grid>
							)}
							{watchAddressLanguage == Language.chinese && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameZh,
											};
										})}
									/>
								</Grid>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='buildingBlockSpecify'
									label={t('preAdmission.blockDetail')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='floor'
									label={t('preAdmission.storey')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='flat'
									label={t('preAdmission.chamber')}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.statement')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.statementTips') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`consentPersonalDataUsage`}
									row
									options={CONSENT}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.recovery') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`consentTWC`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.familyAlliance') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`consentFamilyNetwork`}
									row
									options={ZH_YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='applicationSignatureDate'
									label={t('family.signatureDate') + (isMinor ? '' : '*')}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='guardianSignatureDate'
									label={
										t('family.guardianSignatureDate') + (isMinor ? '*' : '')
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='guardianName'
									label={t('family.guardianName')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='handledBy'
									label={t('family.staffName') + '*'}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='handlingSignatureDate'
									label={t('family.staffSignatureDate') + '*'}
								/>
							</Grid>
						</Grid>

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.serviceUnit')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('family.acceptApplication') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name={`acceptApplication`}
									row
									options={ZH_YESNO}
									handleChange={(e: any) => {
										if (e.target.value !== 'false') {
											methods.setValue('nonAcceptanceReason', '');
											methods.trigger('nonAcceptanceReason');
										}
									}}
								/>
							</Grid>
							<Grid size={6}></Grid>
							{(watchAcceptApplication === false ||
								watchAcceptApplication === 'false') && (
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='nonAcceptanceReason'
										label={t('family.noAdoptReason') + '*'}
									/>
								</Grid>
							)}
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='workerSignatureDate'
									label={t('family.staffSignDate') + '*'}
								/>
							</Grid>
						</Grid>

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('family.remark')}
						</Typography>
						<Grid
							container
							spacing={3}
							marginTop={0}
							marginBottom={3}
							alignItems='center'
						>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									name='pirRemarks'
									label={t('family.rehabiliteeRemark')}
								/>
							</Grid>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									name='applicantRemarks'
									label={t('family.applyRemark')}
								/>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
